import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchWorkspaceKnowledge,
  saveWorkspaceKnowledge,
} from "../api/adminClient";
import { ROUTES } from "./routes";
import WorkspaceKnowledgeInfoButton from "./WorkspaceKnowledgeInfoButton";
import {
  MAX_BRIEF_LEN,
  MAX_THEMES,
  MAX_THEME_LEN,
  normalizeWorkspaceKnowledgePayload,
} from "./workspaceKnowledgeSchema";

function formatSyncedAt(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

const NEXUS_TRY_PROMPTS = [
  "How should we talk about our organization priorities?",
  "What strategic themes should I emphasize when discussing results?",
  "Summarize how NEXUS should describe our company voice.",
];

export default function WorkspaceKnowledgePage({ embedded = false }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [brief, setBrief] = useState("");
  const [themes, setThemes] = useState([]);
  const [themeInput, setThemeInput] = useState("");
  const [preferInput, setPreferInput] = useState("");
  const [avoidInput, setAvoidInput] = useState("");
  const [prefer, setPrefer] = useState([]);
  const [avoid, setAvoid] = useState([]);
  const [indexedAt, setIndexedAt] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loadError, setLoadError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await fetchWorkspaceKnowledge();
      const k = res.data?.knowledge || {};
      setBrief(k.customer_brief || "");
      setThemes(Array.isArray(k.strategic_themes) ? k.strategic_themes : []);
      const vocab = k.vocabulary || {};
      setPrefer(Array.isArray(vocab.prefer) ? vocab.prefer : []);
      setAvoid(Array.isArray(vocab.avoid) ? vocab.avoid : []);
      setIndexedAt(k.indexed_at || null);
      setUpdatedAt(k.updated_at || null);
    } catch (ex) {
      setLoadError(ex.message || "Could not load workspace knowledge");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const addTheme = () => {
    const text = themeInput.trim();
    if (!text || themes.length >= MAX_THEMES) return;
    if (text.length > MAX_THEME_LEN) {
      setError(`Each theme must be at most ${MAX_THEME_LEN} characters`);
      return;
    }
    if (themes.some((t) => t.toLowerCase() === text.toLowerCase())) {
      setThemeInput("");
      return;
    }
    setThemes((prev) => [...prev, text]);
    setThemeInput("");
    setError("");
  };

  const removeTheme = (idx) => {
    setThemes((prev) => prev.filter((_, i) => i !== idx));
  };

  const addVocab = (kind) => {
    const input = kind === "prefer" ? preferInput : avoidInput;
    const text = input.trim();
    if (!text) return;
    const list = kind === "prefer" ? prefer : avoid;
    if (list.length >= 20) return;
    if (list.some((x) => x.toLowerCase() === text.toLowerCase())) {
      if (kind === "prefer") setPreferInput("");
      else setAvoidInput("");
      return;
    }
    if (kind === "prefer") {
      setPrefer((p) => [...p, text]);
      setPreferInput("");
    } else {
      setAvoid((p) => [...p, text]);
      setAvoidInput("");
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const payload = normalizeWorkspaceKnowledgePayload({
        customer_brief: brief,
        strategic_themes: themes,
        vocabulary: { prefer, avoid },
      });
      const res = await saveWorkspaceKnowledge(payload);
      const k = res.data?.knowledge || {};
      setIndexedAt(k.indexed_at || null);
      setUpdatedAt(k.updated_at || null);
      setLoadError("");
      setSuccess("Saved and synced to NEXUS.");
    } catch (ex) {
      setError(ex.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const tryNexus = (prompt) => {
    navigate(ROUTES.dashboard, { state: { nexusPrompt: prompt } });
  };

  if (loading) {
    return (
      <p className="text-sm text-slate-500 py-6" role="status">
        Loading workspace knowledge…
      </p>
    );
  }

  return (
    <div className={embedded ? "" : "w-full px-3 sm:px-5 lg:px-6 py-5 sm:py-6 box-border"}>
      <form onSubmit={handleSave} className="max-w-3xl space-y-6">
        {loadError ? (
          <div className="workspace-knowledge-load-banner" role="alert">
            <p>{loadError}</p>
            <button type="button" onClick={() => load()}>
              Retry
            </button>
          </div>
        ) : null}
        {error ? (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        ) : null}
        {success ? (
          <p className="text-sm text-emerald-700 dark:text-emerald-400" role="status">
            {success}
          </p>
        ) : null}

        <div>
          <div className="flex items-center gap-2 mb-1">
            <label htmlFor="customer-brief" className="text-sm font-medium">
              Customer brief
            </label>
            <WorkspaceKnowledgeInfoButton />
          </div>
          <textarea
            id="customer-brief"
            className="w-full min-h-[140px] rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm"
            value={brief}
            onChange={(ev) => setBrief(ev.target.value)}
            maxLength={MAX_BRIEF_LEN}
            placeholder="Who you are, priorities, tone, topics to emphasize or avoid…"
          />
          <p className="text-xs text-slate-500 mt-1">
            {brief.length}/{MAX_BRIEF_LEN}
          </p>
        </div>

        <div>
          <span className="block text-sm font-medium mb-1">Strategic themes</span>
          <p className="text-xs text-slate-500 mb-2">Named priorities (3–{MAX_THEMES} items).</p>
          <div className="flex flex-wrap gap-2 mb-2">
            {themes.map((t, i) => (
              <span
                key={`${t}-${i}`}
                className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 px-3 py-1 text-sm"
              >
                {t}
                <button
                  type="button"
                  className="text-slate-500 hover:text-slate-800"
                  aria-label={`Remove ${t}`}
                  onClick={() => removeTheme(i)}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              className="flex-1 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm"
              value={themeInput}
              onChange={(ev) => setThemeInput(ev.target.value)}
              maxLength={MAX_THEME_LEN}
              placeholder="Add a theme"
              onKeyDown={(ev) => {
                if (ev.key === "Enter") {
                  ev.preventDefault();
                  addTheme();
                }
              }}
            />
            <button
              type="button"
              className="rounded-lg border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm"
              onClick={addTheme}
              disabled={themes.length >= MAX_THEMES}
            >
              Add
            </button>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <span className="block text-sm font-medium mb-1">Words to prefer</span>
            <VocabList items={prefer} onRemove={(i) => setPrefer((p) => p.filter((_, j) => j !== i))} />
            <VocabInput value={preferInput} onChange={setPreferInput} onAdd={() => addVocab("prefer")} />
          </div>
          <div>
            <span className="block text-sm font-medium mb-1">Words to avoid</span>
            <VocabList items={avoid} onRemove={(i) => setAvoid((p) => p.filter((_, j) => j !== i))} />
            <VocabInput value={avoidInput} onChange={setAvoidInput} onAdd={() => addVocab("avoid")} />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4 pt-2">
          <button
            type="submit"
            disabled={saving}
            className="rounded-lg bg-indigo-600 text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save & sync to NEXUS"}
          </button>
          {indexedAt ? (
            <span className="text-xs text-slate-500">
              Last synced to NEXUS: {formatSyncedAt(indexedAt)}
              {updatedAt && updatedAt !== indexedAt
                ? ` · Edited ${formatSyncedAt(updatedAt)}`
                : ""}
            </span>
          ) : (
            <span className="text-xs text-slate-500">Not synced yet — save to index for NEXUS.</span>
          )}
        </div>

        {indexedAt ? (
          <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
            <p className="text-sm font-medium text-slate-800 dark:text-slate-200 mb-2">Try asking NEXUS</p>
            <p className="text-xs text-slate-500 mb-3">
              Opens the dashboard and runs a sample question using your saved knowledge.
            </p>
            <div className="flex flex-wrap gap-2">
              {NEXUS_TRY_PROMPTS.map((prompt) => (
                <button
                  key={prompt}
                  type="button"
                  className="text-left text-xs rounded-full border border-slate-200 dark:border-slate-600 px-3 py-1.5 hover:border-[#004f73]/40 hover:bg-[#004f73]/[0.04]"
                  onClick={() => tryNexus(prompt)}
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </form>
    </div>
  );
}

function VocabList({ items, onRemove }) {
  if (!items.length) return null;
  return (
    <div className="flex flex-wrap gap-1 mb-2">
      {items.map((t, i) => (
        <span
          key={`${t}-${i}`}
          className="inline-flex items-center gap-1 rounded bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-xs"
        >
          {t}
          <button type="button" className="text-slate-500" onClick={() => onRemove(i)} aria-label={`Remove ${t}`}>
            ×
          </button>
        </span>
      ))}
    </div>
  );
}

function VocabInput({ value, onChange, onAdd }) {
  return (
    <div className="flex gap-2">
      <input
        type="text"
        className="flex-1 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm"
        value={value}
        onChange={(ev) => onChange(ev.target.value)}
        placeholder="Add term"
        onKeyDown={(ev) => {
          if (ev.key === "Enter") {
            ev.preventDefault();
            onAdd();
          }
        }}
      />
      <button type="button" className="text-sm px-2 border rounded-lg" onClick={onAdd}>
        Add
      </button>
    </div>
  );
}
